/**
 * Returning-visitor tracking.
 *
 * A single persistent, ANONYMOUS visitor record in localStorage (no PII — just a
 * random id + counters) lets the backend tell first-time from returning visitors
 * and stitch a person's repeat visits together. Written only after consent (the
 * caller gates this), and fully guarded for SSR / private-mode / disabled storage
 * — visitor tracking silently no-ops rather than ever throwing.
 */
import { uuid } from './util.js';

/* Storage keys carry the brand, and the brand has now changed twice
 * (Lumera → Obsrviq → LogClip). Unlike a header or an env var, these keys live in
 * END USERS' browsers rather than in our infrastructure: renaming them without a
 * migration would make every returning visitor on every customer site look
 * brand new, silently resetting visitor counts, new-vs-returning and every
 * retention cohort. Nothing would error — it would just quietly be wrong.
 *
 * So reads fall through the legacy names (adopting whatever they find under the
 * current one), and writes go to the legacy names TOO. The dual-write matters:
 * a customer site can serve a cached pre-rebrand bundle on one page and the new
 * bundle on another, and a read-only fallback would let those two mint separate
 * visitor records for the same person. Dual-writing keeps both on one record.
 * The legacy writes can be dropped once old bundles have aged out of caches. */
const VISITOR_KEY = 'logclip.visitor';
const SESSION_KEY = 'logclip.session';
const LEGACY_VISITOR_KEYS = ['obsrviq.visitor', 'lumera.visitor'] as const;
const LEGACY_SESSION_KEYS = ['obsrviq.session', 'lumera.session'] as const;

/** Persisted across page loads so a returning visit within the inactivity window
 *  can RESUME the same session (opt-in `continueSession`). Same-origin localStorage
 *  only — graceful no-op when unavailable (→ per-page sessions, today's default). */
export interface SessionRecord {
  /** The session id to continue. */
  id: string;
  /** Epoch ms the (original) session started — the stable clock anchor. */
  startedAt: number;
  /** Epoch ms of the last activity (flush) — the inactivity-gap reference. */
  lastActivity: number;
  /** Next batch seq to send (kept monotonic across page loads → no chunk-key
   *  collision on the (session_id, seq) primary key). */
  seq: number;
  /** Highest event `t` (ms since session start) reached so far — used as a floor
   *  for the resumed clock so the next page's timestamps never overlap the prior
   *  page's, even if the wall clock moves backward between loads. */
  lastT?: number;
  /** The userId this session is attributed to, if any — so a DIFFERENT user on a
   *  shared device starts a fresh session instead of merging. */
  userId?: string;
}

export function readSession(): SessionRecord | null {
  const raw = readBranded(SESSION_KEY, LEGACY_SESSION_KEYS);
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<SessionRecord>;
    if (p && typeof p.id === 'string' && p.id && typeof p.startedAt === 'number') {
      return {
        id: p.id,
        startedAt: p.startedAt,
        lastActivity: typeof p.lastActivity === 'number' ? p.lastActivity : p.startedAt,
        seq: typeof p.seq === 'number' && p.seq >= 0 ? p.seq : 0,
        lastT: typeof p.lastT === 'number' && p.lastT >= 0 ? p.lastT : undefined,
        userId: typeof p.userId === 'string' ? p.userId : undefined,
      };
    }
  } catch {
    /* corrupted — treat as none */
  }
  return null;
}

export function writeSession(rec: SessionRecord): void {
  writeBranded(SESSION_KEY, LEGACY_SESSION_KEYS, JSON.stringify(rec));
}

export function clearSession(): void {
  clearBranded(SESSION_KEY, LEGACY_SESSION_KEYS);
}

export interface VisitorRecord {
  /** Stable anonymous id for this browser. */
  id: string;
  /** Epoch ms first ever seen. */
  firstSeenAt: number;
  /** Epoch ms of the most recent visit (this one, after registerVisit). */
  lastSeenAt: number;
  /** Lifetime visit count, including the current visit. */
  visits: number;
}

function readLS(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // private mode / disabled / sandboxed
  }
}

function writeLS(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota / disabled — visitor tracking degrades to per-session */
  }
}

function removeLS(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Read `key`, falling back through pre-rebrand key names in order. A value found
 *  under a legacy name is adopted under the current one so later reads are direct. */
function readBranded(key: string, legacy: readonly string[]): string | null {
  const own = readLS(key);
  if (own !== null) return own;
  for (const k of legacy) {
    const v = readLS(k);
    if (v !== null) {
      writeLS(key, v);
      return v;
    }
  }
  return null;
}

/** Write `key` and every legacy alias, so a page still running a cached
 *  pre-rebrand bundle shares this record instead of minting a second visitor. */
function writeBranded(key: string, legacy: readonly string[], value: string): void {
  writeLS(key, value);
  for (const k of legacy) writeLS(k, value);
}

/** Clear `key` and every legacy alias — a half-cleared session would be resumed
 *  from the alias by the very next read. */
function clearBranded(key: string, legacy: readonly string[]): void {
  removeLS(key);
  for (const k of legacy) removeLS(k);
}

/**
 * Read-or-create the visitor record and register the current visit (increments
 * the count, stamps lastSeenAt). Returns the snapshot for THIS session: a fresh
 * record has visits === 1 (first-time visitor); an existing one has visits > 1
 * (returning). Never throws.
 */
export function registerVisit(now: number): VisitorRecord {
  let rec: VisitorRecord | null = null;
  const raw = readBranded(VISITOR_KEY, LEGACY_VISITOR_KEYS);
  if (raw) {
    try {
      const p = JSON.parse(raw) as Partial<VisitorRecord>;
      if (p && typeof p.id === 'string' && p.id) {
        rec = {
          id: p.id,
          firstSeenAt: typeof p.firstSeenAt === 'number' ? p.firstSeenAt : now,
          lastSeenAt: typeof p.lastSeenAt === 'number' ? p.lastSeenAt : now,
          visits: typeof p.visits === 'number' && p.visits > 0 ? p.visits : 1,
        };
      }
    } catch {
      /* corrupted JSON — fall through and regenerate */
    }
  }

  const next: VisitorRecord = rec
    ? { ...rec, visits: rec.visits + 1, lastSeenAt: now }
    : { id: uuid(), firstSeenAt: now, lastSeenAt: now, visits: 1 };

  writeBranded(VISITOR_KEY, LEGACY_VISITOR_KEYS, JSON.stringify(next));
  return next;
}
